import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { Bill } from '../../types/billing.types';
import { useBilling } from '../../hooks/useBilling';
import InvoiceMemoEditor from '../../lexical/InvoiceMemoEditor';

interface Props {
  bill: Bill;
  isEditable: boolean;
}

const InvoiceContentTab: React.FC<Props> = ({ bill, isEditable }) => {
  const { updateBill, fetchBill } = useBilling();
  const [description, setDescription] = useState(bill.description || '');
  const memoRef = useRef<Record<string, unknown> | null>(
    bill.memo && typeof bill.memo === 'object' ? bill.memo as Record<string, unknown> : null
  );
  const [customFields, setCustomFields] = useState<Array<{ key: string; value: string }>>(
    bill.custom_fields
      ? Object.entries(bill.custom_fields).map(([key, value]) => ({
          key,
          value: String(value),
        }))
      : []
  );
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDescription(bill.description || '');
    memoRef.current = bill.memo && typeof bill.memo === 'object' ? bill.memo as Record<string, unknown> : null;
    setCustomFields(
      bill.custom_fields
        ? Object.entries(bill.custom_fields).map(([key, value]) => ({
            key,
            value: String(value),
          }))
        : []
    );
    setDirty(false);
  }, [bill.bill_id, bill.description, bill.memo, bill.custom_fields]);

  const handleTextChange = useCallback((text: string) => {
    setDescription(text);
    setDirty(true);
  }, []);

  const handleJsonChange = useCallback((json: Record<string, unknown>) => {
    memoRef.current = json;
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const fields: Record<string, unknown> = {};
      for (const { key, value } of customFields) {
        if (key.trim()) fields[key.trim()] = value;
      }
      await updateBill(bill.bill_id, {
        description: description || undefined,
        memo: memoRef.current || undefined,
      });
      await fetchBill(bill.bill_id);
      setDirty(false);
    } catch {
      // error handled by hook
    } finally {
      setSaving(false);
    }
  }, [dirty, saving, description, customFields, bill.bill_id, updateBill, fetchBill]);

  const addCustomField = () => {
    setCustomFields([...customFields, { key: '', value: '' }]);
    setDirty(true);
  };

  const removeCustomField = (index: number) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
    setDirty(true);
  };

  const updateCustomField = (index: number, field: 'key' | 'value', val: string) => {
    const updated = [...customFields];
    updated[index] = { ...updated[index], [field]: val };
    setCustomFields(updated);
    setDirty(true);
  };

  return (
    <div className="space-y-6">
      {/* Description / Memo — Rich Text Editor */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">
          Invoice Description
        </h3>
        <InvoiceMemoEditor
          key={bill.bill_id}
          initialJson={bill.memo && typeof bill.memo === 'object' ? bill.memo : null}
          initialText={!bill.memo ? (bill.description || '') : undefined}
          onJsonChange={handleJsonChange}
          onTextChange={handleTextChange}
          readOnly={!isEditable}
          placeholder="Enter invoice description, notes, or terms..."
        />
      </div>

      {/* Custom Fields */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Custom Fields
          </h3>
          {isEditable && (
            <button
              onClick={addCustomField}
              className="flex items-center gap-1 text-xs font-medium text-lex-accent hover:opacity-80"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Field
            </button>
          )}
        </div>

        {customFields.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            No custom fields. {isEditable ? 'Click "Add Field" to add custom information.' : ''}
          </p>
        ) : (
          <div className="space-y-2">
            {customFields.map((field, i) => (
              <div key={i} className="flex items-center gap-2">
                {isEditable ? (
                  <>
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => updateCustomField(i, 'key', e.target.value)}
                      placeholder="Field name"
                      className="w-1/3 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                    />
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => updateCustomField(i, 'value', e.target.value)}
                      placeholder="Value"
                      className="flex-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={() => removeCustomField(i)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <div className="flex gap-4 text-sm py-1">
                    <span className="font-medium text-gray-700 dark:text-gray-300 min-w-[150px]">{field.key}</span>
                    <span className="text-gray-900 dark:text-white">{field.value}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save button */}
      {isEditable && dirty && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceContentTab;
